import { debounce } from '@comeon/utility';
import { insertTrackEvent } from '@comeon/analytics';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { selectEnableLiveCasino3 } from '~/components/brand/ducks/selectors';
import { localRecentlyPlayedSelector } from '~/components/casino-shared/ducks/selectors';
import { useCasinoSharedSelector } from '~/components/casino-shared/ducks/types';
import gridTemplates from '~/components/casino-shared/explore/grids/index';
import useFilteredGames from '~/components/casino-shared/filters/use-filtered-games';
import GameCard from '~/components/casino-shared/game-card/game-card';
import WinnerCard from '~/components/casino-shared/game-card/winner-card';
import { ROUTE_TOURNAMENT_LOBBY } from '~/components/casino-shared/tournaments/routes';
import TournamentCard from '~/components/casino-shared/tournaments/tournament-card';
import usePushTransformedPath from '~/components/casino-shared/tournaments/utils/use-push-transformed-path';
import {
	EnhancedCategory,
	EnhancedWinner,
	Tournament,
	type TrackGameClickParamsFromCategory
} from '~/components/casino-shared/types';
import { gamesByGameCodeSelector as casinoGamesByCodeSelector } from '~/components/casino/ducks/selectors';
import Icon, { ICON_RIGHT } from '~/components/icon/index';
import { isSmallerSelector } from '~/components/layout/mediaquery';
import { gamesByGameCodeSelector as liveCasinoGamesByCodeSelector } from '~/components/live-casino/ducks/selectors';
import useTranslations from '~/components/translation/use-translations';
import useCasinoLocation from '~/hooks/use-casino-location';
import { useOnIntersection } from '~/hooks/use-intersection-observer';
import Widget from '../../exclusive-game-widget/widget';
import { getGridType0CardSize } from '../grids/grid-type-0';
import CategoryCarousel from './category-carousel';
import {
	CategoryContainer,
	CategoryInfo,
	CategoryLink,
	CategoryTitle,
	WidgetCategoryContainer
} from './standard-category.styles';

const FALLBACK_GRID_TYPE = 0;
const hasIOSupport = !!window.IntersectionObserver;

const getResultGridType = (
	gridType: keyof typeof gridTemplates,
	numOfItems: number,
	nrMinimumItems: number
): keyof typeof gridTemplates => {
	if (gridType && numOfItems >= nrMinimumItems) {
		return gridType;
	}
	return FALLBACK_GRID_TYPE;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapGridCards = <T extends (params: any, index: number) => any>(
	data: Parameters<T>[0][],
	renderer: T,
	resultGridType: number,
	nrMinimumItems: number
): NonNullable<ReturnType<T>>[] => {
	let numOfCards = 20;
	if (resultGridType !== FALLBACK_GRID_TYPE) {
		numOfCards = data.length - (data.length % nrMinimumItems);
	}
	// eslint-disable-next-line @typescript-eslint/no-unsafe-return
	return (
		data
			?.slice(0, numOfCards)
			.map(renderer)
			.filter(gameCard => !!gameCard) || []
	);
};

const TEXT_KEYS = ['VIEW_ALL'];

type StandardCategoryProps = {
	category: EnhancedCategory;
	openGameInfoPage: (id: string) => void;
	trackGameClick: (trackingData: TrackGameClickParamsFromCategory) => void;
	listName: string;
};

const StandardCategory = ({ category, openGameInfoPage, trackGameClick, listName }: StandardCategoryProps) => {
	const pushTransformedPath = usePushTransformedPath();
	const { isLoadingTranslations, translations } = useTranslations(TEXT_KEYS);

	const itemsContainerRef = useRef<HTMLDivElement | null>(null);
	const categoryContainerRef = useRef<HTMLDivElement | null>(null);

	const [forcedZeroGrid, setForcedZeroGrid] = useState(false);

	const { type, itemDisplayType, id, gameCodes: codes } = category;
	const { isLiveCasinoLocation } = useCasinoLocation();
	const gamesSelector = isLiveCasinoLocation ? liveCasinoGamesByCodeSelector : casinoGamesByCodeSelector;
	const games = useCasinoSharedSelector(gamesSelector);
	const localRecentlyPlayed = useCasinoSharedSelector(localRecentlyPlayedSelector);
	const smaller = useCasinoSharedSelector(isSmallerSelector);
	const [renderContent, setRenderContent] = useState(!hasIOSupport);
	const enableLiveCasino3 = useCasinoSharedSelector(selectEnableLiveCasino3);
	const { getFilteredGames } = useFilteredGames();

	// filtering and ordering the games requires more than just game code, that's why we are mapping twice
	const gameCodes = enableLiveCasino3
		? getFilteredGames(codes?.map(code => games[code])).map(game => game.code)
		: codes;

	const updateRenderElement = useCallback(() => {
		if (!renderContent) {
			setRenderContent(true);
		}
	}, [renderContent]);

	useOnIntersection(categoryContainerRef, updateRenderElement, {
		shouldFreezeOnceVisible: true,
		rootMargin: '5%'
	});

	const checkForcedZeroGrid = useCallback(() => {
		if (!categoryContainerRef.current) return null;

		if (!('gridType' in category)) return null;

		const { gridType } = category;

		const { nrMinimumItems, smallUnitSize, bigUnitSize } = gridTemplates[gridType];
		const gridTypesUnaffectedFromWidth = [3, 4];

		const currentUnitSize = smaller ? smallUnitSize : bigUnitSize;
		const forceZeroGridForABTest = (gridType === 3 || gridType === 4) && !enableLiveCasino3; // will be removed when ab test finished

		if (currentUnitSize && gameCodes) {
			const scrollerWidth = currentUnitSize.width * Math.floor(gameCodes.length / nrMinimumItems);

			const containerWithWithoutPadding =
				categoryContainerRef.current.clientWidth -
				2 * parseFloat(getComputedStyle(categoryContainerRef.current).paddingLeft);

			if (
				(containerWithWithoutPadding > scrollerWidth && !gridTypesUnaffectedFromWidth.includes(gridType)) ||
				forceZeroGridForABTest
			) {
				setForcedZeroGrid(true);
			} else {
				setForcedZeroGrid(false);
			}
		}

		return null;
	}, [gameCodes, category, smaller, enableLiveCasino3]);

	useEffect(() => {
		checkForcedZeroGrid();
	}, [checkForcedZeroGrid]);

	const resizeHandler = debounce(checkForcedZeroGrid, 500);

	useEffect(() => {
		window.addEventListener('resize', resizeHandler);
		window.addEventListener('orientationchange', resizeHandler);
		return () => {
			window.removeEventListener('resize', resizeHandler);
			window.removeEventListener('orientationchange', resizeHandler);
		};
	}, [resizeHandler]);

	const onClickCategoryLink = () => {
		insertTrackEvent({
			category: 'View All',
			action: 'Clicked',
			label: category?.title || ''
		});
		if (type === 'tournaments') {
			pushTransformedPath(ROUTE_TOURNAMENT_LOBBY);
		} else {
			openGameInfoPage(id);
		}
	};

	const winnerCardRenderer = (winner: EnhancedWinner, index: number) => {
		return (
			<WinnerCard
				index={index}
				game={games[winner.gameCode]}
				key={`${winner.gameCode}_${winner.playerName ?? 'anonymous user'}_${winner.winAmount}`}
				winnerDetail={winner}
				trackGameClick={trackGameClick}
				listName={type}
			/>
		);
	};

	const recentlyPlayedCardRenderer = (gameCode: string, index: number) => {
		const game = games[gameCode] || localRecentlyPlayed[gameCode];

		if (!game) return null;

		return (
			<GameCard
				key={gameCode}
				index={index}
				game={game}
				trackGameClick={trackGameClick}
				listName={category?.title ?? ''}
				forceStaticImage={category?.staticImageEnabled}
			/>
		);
	};

	const gridGameCardRenderer = (gameCode: string, index: number) => {
		const game = games[gameCode];
		if (!game) return null;
		return (
			<GameCard
				key={gameCode}
				index={index}
				game={game}
				trackGameClick={trackGameClick}
				listName={category?.title ?? ''}
				forceStaticImage={category.staticImageEnabled}
			/>
		);
	};

	const tournamentCardRenderer = (tournament: Tournament) => {
		return (
			<TournamentCard
				key={tournament.id}
				id={tournament.id}
				name={tournament.name}
				image={tournament.image}
				otherView={tournament.otherView}
				tags={tournament.tags}
				prizePool={tournament.totalPrize}
				prizePoolText={tournament.prizePoolText}
				currencyCode={tournament.currencyCode}
				startDate={tournament.startDate}
				endDate={tournament.endDate}
				isJoined={tournament.userJoined}
			/>
		);
	};

	let cardNodes: ReactElement[] = [];

	let resultGridType: keyof typeof gridTemplates = 0;

	switch (type) {
		case 'winner_feed': {
			cardNodes = category.winners.map(winnerCardRenderer);
			break;
		}
		case 'continue_playing': {
			const gameCodesSet = [...new Set([...Object.keys(localRecentlyPlayed), ...gameCodes])];

			if (isLiveCasinoLocation && enableLiveCasino3) {
				// live Casino should have grid type 3 in continue_playing category
				resultGridType = 3;
				cardNodes = mapGridCards(gameCodesSet, recentlyPlayedCardRenderer, resultGridType, 1);
			} else {
				cardNodes = gameCodesSet
					.map((gameCode, index) => recentlyPlayedCardRenderer(gameCode, index))
					.filter<JSX.Element>(card => !!card);
			}
			break;
		}
		case 'tournaments': {
			cardNodes = category.tournaments.map(tournamentCardRenderer);
			break;
		}
		case 'exclusive_game_widget': {
			break;
		}
		default: {
			resultGridType = getResultGridType(
				category.gridType,
				gameCodes.length,
				gridTemplates[category.gridType].nrMinimumItems
			);

			if (forcedZeroGrid) {
				resultGridType = FALLBACK_GRID_TYPE;
			}

			cardNodes = mapGridCards(
				gameCodes,
				gridGameCardRenderer,
				resultGridType,
				gridTemplates[resultGridType].nrMinimumItems
			);
		}
	}

	const { GridComponent, smallUnitSize, bigUnitSize } = gridTemplates[resultGridType];

	const gridHeight =
		(smaller ? smallUnitSize?.height : bigUnitSize?.height) ||
		getGridType0CardSize({ categoryType: type, itemDisplayType }).height;

	if (type === 'exclusive_game_widget') {
		return (
			<WidgetCategoryContainer data-at="category" data-type={type} ref={categoryContainerRef}>
				{renderContent && (
					<Widget {...category} trackGameClick={trackGameClick} type={type} listName={listName} />
				)}
			</WidgetCategoryContainer>
		);
	}

	return (
		<CategoryContainer
			data-at="category"
			data-type={type}
			ref={categoryContainerRef}
			minHeight={gridHeight}
			isEmpty={cardNodes.length === 0}
		>
			{renderContent && (
				<>
					<CategoryInfo>
						<CategoryTitle data-at="category-title" onClick={onClickCategoryLink}>
							{category?.title ?? ''}
						</CategoryTitle>
						{type !== 'winner_feed' && (
							<CategoryLink onClick={onClickCategoryLink} data-at="view-all">
								{!isLoadingTranslations && translations.VIEW_ALL}
								<Icon type={ICON_RIGHT} width={16} height={16} />
							</CategoryLink>
						)}
					</CategoryInfo>

					<CategoryCarousel>
						<div ref={itemsContainerRef}>
							<GridComponent
								allowIncompleteUnits={type === 'continue_playing' && isLiveCasinoLocation}
								GameCards={cardNodes}
								categoryType={type}
								itemDisplayType={itemDisplayType}
							/>
						</div>
					</CategoryCarousel>
				</>
			)}
		</CategoryContainer>
	);
};

export default StandardCategory;
